import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Tooltip, makeStyles, createStyles, Theme, Box } from '@material-ui/core';
import LocationCityIcon from '@material-ui/icons/LocationCity';
import { defineMessages, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';

import CityStatsChessboard from './CityStatsChessboard';
import { useStores } from '../../Stores/useStores';
import citySilhouetteWithGraphs from '../../assets/citySilhouetteWithGraphs.svg';

const CityStatsButtonMessages = defineMessages({
    cityStatsTooltip: {
        id: 'CITY_STATS_TOOLTIP',
        defaultMessage: 'Odpadové statistiky města/obce',
    },
});

interface CityStatsButtonProps {
    className?: string;
}

const CityStatsButton: React.FC<CityStatsButtonProps> = ({ className }) => {
    const classes = useStyles();
    const intl = useIntl();
    const { homeStore } = useStores();
    
    // Získání dat o městě přímo z HomeStore
    const cityData = homeStore.cityInfo;
    const loading = homeStore.cityInfoLoading;
    
    // Stav pro zobrazení dialogu se statistikami města
    const [cityStatsOpen, setCityStatsOpen] = useState(false);
    
    // Unikátní klíč pro CityStatsChessboard, který se změní při každém načtení nových dat
    const [chessboardKey, setChessboardKey] = useState<number>(0);
    
    // Reference na předchozí cityId pro detekci změn
    const prevCityIdRef = useRef<string | number | null>(null);
    
    // Logování pro debugging
    useEffect(() => {
        console.log('CityStatsButton - cityData from HomeStore:', cityData);
        console.log('CityStatsButton - loading from HomeStore:', loading);
        
        // Pokud se změnilo cityId, aktualizujeme klíč pro CityStatsChessboard
        if (cityData && prevCityIdRef.current !== cityData.cityId) {
            console.log('CityStatsButton - City ID changed from', prevCityIdRef.current, 'to', cityData.cityId);
            prevCityIdRef.current = cityData.cityId;
            setChessboardKey(prev => prev + 1);
        }
    }, [cityData, loading]);
    
    const onCityStatsButtonClicked = () => {
        // Otevřít dialog se statistikami města
        // Není potřeba volat refreshCityData, protože HomeStore již udržuje aktuální data
        setCityStatsOpen(true);
    };
    
    const handleCityStatsClose = () => {
        setCityStatsOpen(false);
    };
    
    // Pokud se data načítají nebo nejsou k dispozici, nezobrazovat tlačítko
    if (loading) {
        console.log('CityStatsButton - Still loading, not rendering button');
        return null;
    }
    
    if (!cityData) {
        console.log('CityStatsButton - No city data available, not rendering button');
        return null;
    }
    
    console.log('CityStatsButton - Rendering button with city:', cityData.city);
    
    return (
        <>
            <div className={className}>
                <Tooltip title={intl.formatMessage(CityStatsButtonMessages.cityStatsTooltip)}>
                    <IconButton
                        color="primary"
                        aria-label="city statistics"
                        onClick={onCityStatsButtonClicked}
                        className={classes.cityStatsButton}
                    >
                        <Box className={classes.iconContainer}>
                            <img 
                                src={citySilhouetteWithGraphs} 
                                alt="City statistics" 
                                className={classes.cityGraphIcon} 
                            />
                            <LocationCityIcon className={classes.overlayIcon} />
                        </Box>
                    </IconButton>
                </Tooltip>
            </div>
            
            {/* Dialog se statistikami města - s unikátním klíčem pro vynucení překreslení */}
            <CityStatsChessboard 
                key={chessboardKey}
                cityId={String(cityData.cityId)}
                cityName={cityData.city}
                open={cityStatsOpen}
                onClose={handleCityStatsClose}
            />
        </>
    );
};

export default observer(CityStatsButton);

const useStyles = makeStyles((theme: Theme) => createStyles({
    cityStatsButton: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        '&:hover': {
            backgroundColor: theme.palette.background.default,
        },
        zIndex: 1000, // Zajistí, že tlačítko bude nad ostatními prvky
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(0.5),
    },
    iconContainer: {
        position: 'relative',
        width: 48,
        height: 48,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    cityGraphIcon: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        transition: 'transform 0.3s ease-in-out',
        '&:hover': {
            transform: 'scale(1.3)',
        },
    },
    overlayIcon: {
        position: 'absolute',
        fontSize: '1.5rem',
        color: theme.palette.primary.main,
        opacity: 0.5,
        transition: 'opacity 0.3s ease-in-out',
        '&:hover': {
            opacity: 0.8,
        },
    },
}));